import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellClickedEvent, ColDef } from "ag-grid-community";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router";
import ReactDOMServer from "react-dom/server";
import { AgGridReact } from "ag-grid-react";
import { Dialog } from "@mui/material";
import { toast } from "react-toastify";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import { agConfigDashboardSideBar } from "../dashboardPage/AgGridConfig";

import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { Box, HStack, useGetCompanyName } from "../../components/utils";

import { useAuthStore } from "../../store";
import {
  useGetOnFilterState,
  usePostOnFilterState,
} from "../dashboardPage/AgGridCacheQuery";
import { tableCellStyle } from "../../components/TableComponent";

import { useAgGridTableState } from "../../store/agGridTableStore";

import { useNewShareholderInstrumentStore } from "../../store/newShareholderAndInstrumentStore";
import {
  AmountRender,
  NameRender,
  NumberOfSharesRender,
  SecurityClassRender,
} from "./AddBuybackAgComponent";
import { EmptyTable } from "../../components/shared/EmptyTable";
import AddBuybackPopup from "./AddBuybackPopup";
import { handleEventForTracking } from "../../amplitudeAnalytics";

export type AddBuyBackTransactionModel = {
  uuid: string;
  name: string;
  securityClass: string;
  noOfShares: number;
  amount: number;
};

export type BuybackTransactionDataModel = {
  uuid?: String;
  name: string;
  investorType?: string;
  security: string;
  securityClass: string;
  sharePrice?: number;
  instrumentClassId?: string;
  investorId?: string;
  numberOfShares: number;
  amountInvested: number;
  par?: number;
  conversionRatio?: string;
};

function AgGridQuickBuybackTable() {
  const currencySymbol = getCurrencySymbol();
  const companyName = useGetCompanyName();
  const companyId = useAuthStore().companyId ?? "";
  const userId = useAuthStore().user?.userId ?? 0;
  const gridApi = useRef<any>(null);

  const [addDialog, setAddDialog] = useState<{
    open: boolean;
    name?: string;
    data?: any;
  }>({
    open: false,
  });
  const [displayedRowCount, setDisplayedRowCount] = useState<number>(0);

  const buybackTransactionStore = useNewShareholderInstrumentStore();
  useEffect(() => {
    const count = gridApi.current?.api.getDisplayedRowCount();

    if (count !== undefined) {
      setDisplayedRowCount(count);
    }
  }, [buybackTransactionStore.buyback]);

  const handleAction = (
    action: any,
    buyBack: BuybackTransactionDataModel,
    index: number
  ) => {
    if (action.name === "Delete") {
      toast("Transaction deleted Successfully!", {
        type: "error",
        autoClose: 2000,
      });
      const buyBackTransactions = buybackTransactionStore.buyback.slice();
      buyBackTransactions.splice(index, 1);
      buybackTransactionStore.setBuyback(buyBackTransactions);
    } else if (action.name === "Edit") {
      setAddDialog({
        open: true,
        name: "Buyback",
        data: buyBack,
      });
    }
  };

  const { refetch, data: filterState } = useGetOnFilterState(
    `${userId}`,
    companyId
  );
  const agTableStore = useAgGridTableState();
  useEffect(() => {
    refetch().then((data) => {
      if (data.data) agTableStore.setState(data.data);
      setColumnSetting(data.data?.quickBuybackColumnModel);
      setFilterSetting(data.data?.quickBuybackFilterModel);
    });
  }, []);

  const { mutate: postOnFilter } = usePostOnFilterState();

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState();
    return {};
  }

  function setColumnSetting(model: any) {
    if (gridApi.current)
      gridApi.current.columnApi.applyColumnState({ state: model });
    return {};
  }

  // Gets filter model via the grid API
  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel();
    return {};
  };

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model);
    return {};
  };

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting();
    const filterState = await getFilterSetting();
    postOnFilter({
      userId: `${userId}`,
      companyId,
      filterData: {
        ...agTableStore.state,
        quickBuybackColumnModel: columnState,
        quickBuybackFilterModel: filterState,
      },
    });
  };

  const cin = "";

  const componentsRegistery = useMemo(
    () => ({
      name: memo(NameRender),
      securityClass: memo(SecurityClassRender),
      numberOfShares: memo(NumberOfSharesRender),
      amount: memo(AmountRender),
    }),
    []
  );

  const navigate = useNavigate();

  const onClickShareholderName = (id: string) => {
    handleEventForTracking({ eventName: `Shareholder Name` });
    navigate(`/shareholders/individual/${id}`);
  };

  const isValidDate = (date: string) =>
    Number.isNaN(date) &&
    new Date(date).toString() !== "Invalid Date" &&
    !Number.isNaN(Date.parse(date));

  function DeleteButtonRenderer(props: any) {
    const handleDeleteClick = () => {
      const { api, node } = props;
      const selectedData = node.data;
      const index = buybackTransactionStore.buyback.findIndex(
        (transaction) => transaction === selectedData
      );
      const buyBackTransactions = buybackTransactionStore.buyback.slice();
      buyBackTransactions.splice(index, 1);
      buybackTransactionStore.setBuyback(buyBackTransactions);
    };

    return (
      <div className="items-center pl-10">
        <Icon
          width={20}
          className="cursor-pointer text-gray-400 hover:text-orange-501"
          icon={"ic:baseline-delete"}
          onClick={handleDeleteClick}
        />
      </div>
    );
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Name",
        field: "name",
        cellRenderer: "name",
        initialWidth: 250,
        cellStyle: tableCellStyle,
        sortable: false,
        filter: "agTextColumnFilter",
        tooltipField: "tooltipName",
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "Security Class",
        field: "securityClass",
        filter: "agSetColumnFilter",
        cellRenderer: "securityClass",
        cellStyle: tableCellStyle,
        sortable: false,
        tooltipField: "tooltipSecurityClass",
        menuTabs: ["filterMenuTab"],
      },
      {
        headerName: "No. of Shares",
        field: "numberOfShares",
        filter: "agNumberColumnFilter",
        cellRenderer: "numberOfShares",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipNumberOfShares",
      },
      {
        headerName: `Amount (${currencySymbol})`,
        field: "amount",
        filter: "agNumberColumnFilter",
        cellRenderer: "amount",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipAmount",
      },

      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        menuTabs: [],
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        cellStyle: { "padding-top": "12px", "line-height": "10px" },
        resizable: false,
        sortable: false,
        cellRendererParams: ({ value }: { value: any }) => value?.props,
        cellRenderer: CTADropdown,
      },
    ],
    []
  );

  const gridOptions = {
    suppressRowVirtualisation: true, // Add this line to disable row virtualization
    paginationAutoPageSize: false, // Optional: Set to false if using custom pagination
    suppressScrollOnNewData: true, // Optional: Set to true to prevent scrolling to the top on
  };
  const currencyType = getCurrencyType();

  const rowData = useMemo(
    () =>
      buybackTransactionStore?.buyback.map((template, index) => ({
        uuid: template.uuid,
        name: template.name,
        tooltipName: template.name,
        securityClass: template.securityClass,
        tooltipSecurityClass: template.securityClass,
        numberOfShares: template.numberOfShares,
        tooltipNumberOfShares:
          template.numberOfShares.toLocaleString(currencyType),
        amount: template.amountInvested,
        tooltipAmount: template.amountInvested.toLocaleString(currencyType),
        security: template.security,
        currencySymbol,
        currencyType,
        actions: (
          <CTADropdown
            actions={[
              {
                name: "Edit",
                disabled: false,
              },
              {
                name: "Delete",
                disabled: false,
              },
            ]}
            onAction={(action) => handleAction(action, template, index)}
          />
        ),
      })),
    [buybackTransactionStore.buyback]
  );

  function setTableHeight() {
    if (displayedRowCount === 1) {
      return (displayedRowCount + 2.0) * 60;
    } else if (displayedRowCount === 2) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 3) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 4) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 5) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 6) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 7) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 8) {
      return (displayedRowCount + 1.8) * 60;
    } else if (displayedRowCount === 9) {
      return (displayedRowCount + 1.8) * 60;
    } else {
      return 10 * 60;
    }
  }

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== "action-column") {
      const template = cellParams.data;
      if (cellParams.column.getColId() === "shareholderName") {
        onClickShareholderName(template.id);
      }
    }
  };

  const [filteredRowData, setFilteredRowData] = useState<any>([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);

    const filteredData = grid.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);
    setFilteredRowData(filteredData);
    uploadFilterAndColumn();
  };
  return (
    <div>
      <HStack className="flex-col lg:flex-row justify-start lg:justify-between  py-2 lg:items-center items-start  bg-white rounded-md"></HStack>
      <HStack className="justify-between w-full">
        <Dialog open={addDialog.open} maxWidth="md">
          <AddBuybackPopup
            onPrimaryAction={() =>
              setAddDialog({ open: false, name: "Buyback" })
            }
            onSecondaryAction={() =>
              setAddDialog({ open: false, name: "Buyback" })
            }
            data={addDialog.data}
            mode={"Edit"}
          />
        </Dialog>
        <Box
          style={{
            height: setTableHeight(),
          }}
          className="w-full max-h-full overflow-x-auto ag-theme-material h-full "
        >
          <AgGridReact
            sideBar={agConfigDashboardSideBar}
            onGridReady={(params) => {
              // Store the grid API referen
              gridApi.current = params;
            }}
            components={componentsRegistery}
            alwaysShowHorizontalScroll
            alwaysMultiSort
            animateRows={true}
            enableCharts={true}
            enableRangeSelection={true}
            defaultColDef={defaultColDef}
            onFilterChanged={onAgGridFilterChanged}
            onColumnEverythingChanged={uploadFilterAndColumn}
            rowData={rowData}
            onCellClicked={handleCellClick}
            columnDefs={columnDefs}
            pagination={false}
            suppressRowTransform={true}
            suppressCopyRowsToClipboard={true}
            suppressCopySingleCellRanges={true}
            suppressCellFocus={true}
            suppressMenuHide={false}
            onColumnResized={uploadFilterAndColumn}
            tooltipShowDelay={1000}
            tooltipInteraction={true}
            rowClass={"border-t border-dashed "}
            overlayNoRowsTemplate={ReactDOMServer.renderToString(
              <EmptyTable
                header="No Transaction"
                subHeader="Get started by adding a new transaction"
              />
            )}
            gridOptions={gridOptions}
            getRowStyle={(params) => {
              if (params.rowIndex % 2 === 0) {
                return { background: "#f8f8f8" };
              } else {
                return { background: "#ffffff" };
              }
            }}
          ></AgGridReact>
        </Box>
      </HStack>
    </div>
  );
}

export default AgGridQuickBuybackTable;
